import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import type {
  IActiveBalance,
  IBalanceAccount,
  IBalanceCurrency,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/balance';
import type {
  IAccountBalanceStateProps,
  IChangeCurrencyEventTracking,
} from '@app/dashboard/types/innerLayout/balance_and_transfers';
import {
  ALLOWED_PATHS,
  PARAMS,
  SESSION_KEYS,
} from '@app/dashboard/constants/balance_and_transfers';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { OUTER_ROUTES } from '@app/dashboard/routes/outer-routes';
import { useLangStore } from '@app/dashboard/store/lang';
import { getBankAccountDetails } from '@app/dashboard/services/inner_layout/balance_and_transfers';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';
import { ONE_MINUTE_MS } from '@app/dashboard/constants/shared';

import { getLastActionFromLocalStorage } from '../utils/events-tracking';

export const useActiveBalance = ({
  activeBalance,
  setActiveBalance,
}: IAccountBalanceStateProps) => {
  const firstMountedRef = useRef(true);
  const isAccountsChangedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { lang } = useLangStore();

  const accountIdFromUrl = searchParams.get(PARAMS.ACCOUNT);
  const currencyFromUrl = searchParams.get(PARAMS.CURRENCY);
  const hasCurrencyAndAccountParams = !!currencyFromUrl && !!accountIdFromUrl;

  const haveAccountChanged = isAccountsChangedRef.current
    ? activeBalance?.account?.id
    : Number(accountIdFromUrl) || undefined;

  const haveCurrencyChanged = isAccountsChangedRef.current
    ? activeBalance?.currency
    : currencyFromUrl || undefined;

  const queryResults = useQuery({
    queryKey: [
      QUERY_KEYS.GET_BANK_ACCOUNT_DETAILS,
      lang,
      haveAccountChanged,
      haveCurrencyChanged,
    ],
    queryFn: ({ signal }) => getBankAccountDetails(haveAccountChanged, signal),
    select: (response) =>
      response?.data?.data?.currencies as IBalanceCurrency[],
    placeholderData: (previousData) => previousData,
  });

  const { data: currencies, isFetching } = queryResults;

  const hasParamsValid = useCallback(() => {
    if (!currencies || !hasCurrencyAndAccountParams) return false;

    const isUrlParamsValid = currencies.some(
      ({ currency, accounts }) =>
        currency === currencyFromUrl &&
        accounts.some((account) => account.id.toString() === accountIdFromUrl)
    );

    return isUrlParamsValid;
  }, [currencies, currencyFromUrl, accountIdFromUrl]);

  const handleRemoveParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);

        [PARAMS.ACCOUNT, PARAMS.CURRENCY].forEach((param) => {
          newParams.delete(param);
        });

        return newParams;
      },
      { replace: true }
    );
  }, []);

  const accountSelectedEventTracking = (
    account: IBalanceAccount,
    currency: string
  ) => {
    amplitude.track(BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.ACCOUNT_SELECTED, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.CURRENCY]: currency,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.SELECTED_BANK_ACCOUNT]:
        account?.id,
      [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
    });
  };

  const changeCurrencyEventTracking = ({
    oldCurrency,
    newCurrency,
    ActionTakenBefore,
    TimeSpent,
    switchedCount,
  }: IChangeCurrencyEventTracking) => {
    amplitude.track(
      BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.CURRENCY_TAB_SWITCHED,
      {
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.FROM_CURRENCY]:
          oldCurrency,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE.TO_CURRENCY]:
          newCurrency,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE
          .ACTION_TAKEN_BEFORE_SWITCH]: ActionTakenBefore,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE
          .TIME_SPENT_ON_CURRENCY_MINUTES]: TimeSpent,
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.BALANCE_PAGE
          .SWITCH_COUNT_IN_SESSION]: switchedCount,
        [SHARED_EVENTS_PARAMS.PAGE]: getCurrentPath(),
      }
    );
  };

  const handleTrackChangeCurrency = (currency: string) => {
    if (currency !== activeBalance?.currency) {
      const now = Date.now();

      const previousCount = Number(
        sessionStorage.getItem(SESSION_KEYS.SWITCHED_COUNT) || '0'
      );
      const lastEntryTimestampStored =
        sessionStorage.getItem(SESSION_KEYS.TIME_SPENT) ?? now.toString();
      const actionTakenBefore = getLastActionFromLocalStorage();

      const timeSpentMs = now - Number(lastEntryTimestampStored);
      const timeSpentMinutes = timeSpentMs / ONE_MINUTE_MS;
      const newSwitchCount = previousCount + 1;

      changeCurrencyEventTracking({
        oldCurrency: activeBalance?.currency || '',
        newCurrency: currency,
        ActionTakenBefore: actionTakenBefore,
        TimeSpent: Number(timeSpentMinutes.toFixed(2)),
        switchedCount: newSwitchCount,
      });

      sessionStorage.setItem(SESSION_KEYS.TIME_SPENT, now.toString());
      sessionStorage.setItem(
        SESSION_KEYS.SWITCHED_COUNT,
        newSwitchCount.toString()
      );
    }
  };

  const onChangeActiveBalance = (
    account: IBalanceAccount,
    currency: string
  ) => {
    isAccountsChangedRef.current = true;

    if (account.id === activeBalance?.account?.id) return;

    handleTrackChangeCurrency(currency);

    accountSelectedEventTracking(account, currency);

    setActiveBalance({ currency, account, haveAccounts: !!currencies?.length, isUrlManuallyChanged: false });

    setSearchParams((prev) => {
      prev.set(PARAMS.ACCOUNT, account.id.toString());
      prev.set(PARAMS.CURRENCY, currency);
      return prev;
    });
  };

  useEffect(() => {
    if (isFetching || !firstMountedRef.current) return;

    firstMountedRef.current = false;

    if (currencies?.length) {
      const selectedAccount: IActiveBalance | null = {
        account: null,
        currency: null,
        haveAccounts: false,
      };

      outer: for (const merchantCurrency of currencies) {
        if (merchantCurrency.selected) {
          selectedAccount.currency = merchantCurrency.currency;
        }

        for (const merchantAccount of merchantCurrency.accounts) {
          if (merchantAccount.selected) {
            selectedAccount.account = merchantAccount;
            selectedAccount.haveAccounts = true;
            selectedAccount.isUrlManuallyChanged = false;
          }

          if (
            hasParamsValid() &&
            merchantCurrency.currency === currencyFromUrl &&
            merchantAccount.id.toString() === accountIdFromUrl
          ) {
            selectedAccount.currency = merchantCurrency.currency;
            selectedAccount.account = merchantAccount;
            selectedAccount.haveAccounts = true;
            selectedAccount.isUrlManuallyChanged = false;
            break outer;
          } else if (!hasParamsValid() && hasCurrencyAndAccountParams) {
            firstMountedRef.current = true;
            selectedAccount.haveAccounts = false;
            selectedAccount.isUrlManuallyChanged = true;
            handleRemoveParams();
            break outer;
          }
        }
      }

      setActiveBalance({
        account: selectedAccount.account,
        currency: selectedAccount.currency,
        haveAccounts: selectedAccount.haveAccounts,
        isUrlManuallyChanged: selectedAccount.isUrlManuallyChanged,
      });
    } else {
      setActiveBalance({
        account: null,
        currency: null,
        haveAccounts: false,
        isUrlManuallyChanged: false,
      });
    }
  }, [currencies, isFetching]);

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      const pathname = location.pathname;
      const isAllowedPath = ALLOWED_PATHS.some((path) =>
        pathname.includes(path)
      );

      if (!isAllowedPath) {
        handleRemoveParams();
        navigate(pathname, { replace: true });
      }

      setTimeout(() => {
        const currentPathname = location.pathname;
        if (currentPathname === OUTER_ROUTES.LOGIN) {
          handleRemoveParams();
        }
      }, 0);
    };
  }, [location.pathname]);

  return {
    onChangeActiveBalance,
    queryResults,
    haveAccountChanged,
    haveCurrencyChanged,
  };
};
